// Modal "Nueva cita" de AgendaPage.tsx (Rediseño mayor 2026-09-02, Fase 4
// "destinos reales del nav") — paciente (buscador inline sobre
// GET /api/hce/pacientes, mismo criterio que PacientesPage.tsx) +
// especialidad propia + fecha/hora libres (cal.com es la única fuente
// real de disponibilidad en todo el sistema — no hay selector de horarios
// disponibles en ningún lado, ni en el CRM, así que tampoco se inventa
// uno acá) + notas opcionales.
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  crearCitaHce,
  listarPacientesHce,
  obtenerMisEspecialidades,
  type EspecialidadHce,
  type PacienteHce,
} from '@/lib/hceVault';

export function NuevaCitaDialog({ abierto, onCerrar, onCreada }: { abierto: boolean; onCerrar: () => void; onCreada: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [pacientes, setPacientes] = useState<PacienteHce[] | null>(null);
  const [especialidades, setEspecialidades] = useState<EspecialidadHce[] | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [pacienteElegido, setPacienteElegido] = useState<PacienteHce | null>(null);
  const [specialtyId, setSpecialtyId] = useState('');
  const [fechaHora, setFechaHora] = useState('');
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setError(null);
    listarPacientesHce()
      .then(setPacientes)
      .catch(() => setPacientes([]));
    obtenerMisEspecialidades()
      .then((r) => {
        setEspecialidades(r);
        if (r.length === 1) setSpecialtyId(r[0]!.id);
      })
      .catch(() => setEspecialidades([]));
  }, [abierto]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = (pacientes ?? []).filter((p) => p.medplumPatientId);
    if (!q) return lista.slice(0, 8);
    return lista.filter((p) => p.nombre.toLowerCase().includes(q) || p.idNumber.includes(q)).slice(0, 8);
  }, [pacientes, busqueda]);

  function reset(): void {
    setBusqueda('');
    setPacienteElegido(null);
    setSpecialtyId('');
    setFechaHora('');
    setNotas('');
    setError(null);
  }

  async function confirmar(): Promise<void> {
    if (!pacienteElegido || !specialtyId || !fechaHora) return;
    setEnviando(true);
    setError(null);
    const resultado = await crearCitaHce({
      patientId: pacienteElegido.patientId,
      contactName: pacienteElegido.nombre,
      phone: pacienteElegido.phone,
      startsAtIso: new Date(fechaHora).toISOString(),
      specialtyId,
      notes: notas.trim() || undefined,
    });
    setEnviando(false);
    if (!resultado.ok) {
      setError(resultado.detail);
      return;
    }
    reset();
    onCreada();
  }

  return (
    <Dialog
      open={abierto}
      onOpenChange={(v) => {
        if (v) return;
        reset();
        onCerrar();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('agenda.nuevaCita')}</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="red">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!pacienteElegido ? (
          <div className="space-y-2">
            <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder={t('pacientes.buscarPlaceholder')} />
            <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {filtrados.length === 0 && <li className="p-2 text-sm text-hce-muted">{t('pacientes.sinResultados')}</li>}
              {filtrados.map((p) => (
                <li key={p.patientId}>
                  <button
                    type="button"
                    className="w-full text-left p-2 text-sm hover:bg-accent"
                    onClick={() => setPacienteElegido(p)}
                  >
                    {p.nombre} — {p.idNumber}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-border p-2 text-sm">
              <span className="text-hce-ink">{pacienteElegido.nombre}</span>
              <Button variant="ghost" size="sm" onClick={() => setPacienteElegido(null)}>
                {t('agenda.cambiarPaciente')}
              </Button>
            </div>

            <Select value={specialtyId} onValueChange={setSpecialtyId}>
              <SelectTrigger>
                <SelectValue placeholder={t('agenda.especialidad')} />
              </SelectTrigger>
              <SelectContent>
                {(especialidades ?? []).map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {especialidades?.length === 0 && <p className="text-xs text-destructive">{t('agenda.sinEspecialidad')}</p>}

            <Input type="datetime-local" value={fechaHora} onChange={(e) => setFechaHora(e.target.value)} />
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder={t('agenda.notasPlaceholder')} rows={2} />

            <div className="flex justify-end">
              <Button disabled={!specialtyId || !fechaHora || enviando} onClick={() => void confirmar()}>
                {enviando ? t('agenda.reservando') : t('agenda.reservar')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
